/**
 * The world clock — turning played time into a date and a time of day (T103).
 *
 * **A game day is 20 real minutes** on a **365-day calendar that repeats with no
 * year shown**, and a new world starts on **25 May**. Decided 2026-08-10:
 * **D41**, [ADR-0007](../../../Project/adr/0007-world-clock-and-growth.md) §1,
 * with the implementation notes in `Development/02_Runtime_Architecture.md` §12.
 *
 * ## Why this file is pure arithmetic and holds no state
 *
 * The clock is one number — **how much time has been played** — and everything
 * else is derived from it. Keeping the derivation here, with no timers and no
 * mutable state, means:
 *
 * - the server and the client compute the same date from the same number, so
 *   they cannot disagree about what day it is;
 * - every rule in it is testable without a socket, a database or a clock;
 * - growth later becomes one subtraction over the same day numbers (§12.2), not
 *   a second time system that has to be kept in step with this one.
 *
 * ⚠️ **Real time enters this file nowhere.** Who is allowed to advance the
 * number, and when, is the server's business — see `packages/server/src/clock.ts`.
 */

import { t, tAnyKey } from './i18n.ts';

/**
 * How long one game day lasts, in real milliseconds.
 *
 * **20 minutes — 72× real time, three game days per real hour.** ⚠️ It is
 * Minecraft's exact figure (24,000 ticks at 20 per second) and that is
 * deliberate: every pacing number in `Development/Minecraft/` is calibrated to a
 * 20-minute day, so it transfers to us without being re-derived.
 */
export const REAL_MS_PER_GAME_DAY = 20 * 60 * 1000;

/**
 * Days in the calendar year.
 *
 * ⚠️ **365, always.** The calendar repeats and shows no year, so there is no
 * such thing here as a leap year — which is also why the archived weather year
 * chosen in D46 had to be a non-leap one (2025).
 */
export const DAYS_PER_YEAR = 365;

/** Lengths of the twelve months, in a year that never leaps. */
const MONTH_LENGTHS = [31, 28, 31, 30, 31, 30, 31, 31, 30, 31, 30, 31] as const;

/**
 * Month names live in `lang/en.json` as `clock.month.1` … `clock.month.12`.
 *
 * ✅ **The comment that used to sit here said *"not run through i18n keys… D7 is
 * owed here… adding it for one row would be a system with a single user."* That
 * was right, and it stopped being right the moment the system existed (T124).**
 *
 * ⚠️ **The values are still Icelandic in the English file, on purpose**, exactly
 * as the HUD shows Icelandic block names beside English row labels
 * (`Standing on · Mosahraun`). A translator may leave them alone or not; what
 * matters is that it is now *their* choice and not a decision welded into the
 * code.
 */
const MONTH_KEY_PREFIX = 'clock.month.';

/**
 * Which day of the year a date falls on, 1–365.
 *
 * Exported because it is what pins {@link WORLD_START_DAY_OF_YEAR} to an actual
 * date rather than to a number somebody counted by hand once.
 *
 * @param month 1–12
 * @param dayOfMonth 1–31
 */
export function dayOfYearFor(month: number, dayOfMonth: number): number {
  let day = dayOfMonth;
  for (let m = 1; m < month; m++) day += MONTH_LENGTHS[m - 1] ?? 0;
  return day;
}

/**
 * Where a new world starts: **25 May** (D41).
 *
 * ⚠️ Not an arbitrary date. ADR-0007: it puts a new player's first hour **in
 * daylight and in the growing season**, so the first thing anybody tries
 * actually works. Starting on 1 January would have given them three and a half
 * minutes of light and nothing that grows.
 */
export const WORLD_START_DAY_OF_YEAR = dayOfYearFor(5, 25);

/**
 * The time of day a new world starts at, as a fraction of a day: **06:00**.
 *
 * ⚠️ **Decided here, and it was not in the decision.** D41 fixes the date and
 * says nothing about the hour. Morning is the useful answer — it is what
 * Minecraft does with a new world, and it means the clock is visibly running
 * *forwards through a day* within the first minute of playing rather than
 * rolling midnight immediately. Change it here if it reads wrong; nothing else
 * depends on the value.
 */
export const WORLD_START_DAY_FRACTION = 6 / 24;

/** A moment in the world, derived from played time. */
export interface WorldTime {
  /**
   * Which day of the world this is, counting from **1** and never resetting.
   *
   * 🔴 **This is the number the player is never shown and the world cannot do
   * without.** The calendar repeats every 365 days, so a date alone cannot
   * answer *"how long ago was this wood cleared?"* — four years ago and this
   * morning are the same date. Anything with a duration stores one of these and
   * subtracts (§12.2).
   */
  readonly absoluteDay: number;
  /** Day of the repeating calendar year, 1–365. */
  readonly dayOfYear: number;
  /** 1–12. */
  readonly month: number;
  /** 1–31. */
  readonly dayOfMonth: number;
  /** 0–23. */
  readonly hour: number;
  /** 0–59. */
  readonly minute: number;
  /**
   * How far through the day it is: **0 at midnight, 0.5 at noon**.
   *
   * Here because it is what the sky will want (piece ② of T103) — a sun angle
   * is a fraction of a day, not an hour and a minute.
   */
  readonly dayFraction: number;
}

/**
 * What day and time it is, given how long the world has been played.
 *
 * @param playedMs accumulated **played** real milliseconds — never wall-clock
 *   time since the world was created. The clock stops when nobody is signed in
 *   (D41), so the two diverge by however long the server sat empty, which on a
 *   family server is most of the week.
 */
export function worldTime(playedMs: number): WorldTime {
  /* ⚠️ A negative or non-finite total is a bug upstream, and the honest thing
     to do with it is to show the start of the world rather than a date built
     out of NaN — which formats as "NaN. undefined" and looks like a rendering
     fault rather than a clock fault. */
  const played = Number.isFinite(playedMs) && playedMs > 0 ? playedMs : 0;

  /* Days since the world began, including the fraction of the current one, and
     offset by the hour a new world starts at. */
  const total = WORLD_START_DAY_FRACTION + played / REAL_MS_PER_GAME_DAY;

  const daysElapsed = Math.floor(total);
  const dayFraction = total - daysElapsed;

  /* ⚠️ 1-based on purpose: the world's first day is day 1, and there is then no
     off-by-one between the number in the code and the number on the screen.
     Growth is unaffected — it subtracts two of these, and a shared offset
     cancels. */
  const absoluteDay = daysElapsed + 1;
  const dayOfYear = ((WORLD_START_DAY_OF_YEAR - 1 + daysElapsed) % DAYS_PER_YEAR) + 1;

  let month = 1;
  let dayOfMonth = dayOfYear;
  for (const length of MONTH_LENGTHS) {
    if (dayOfMonth <= length) break;
    dayOfMonth -= length;
    month++;
  }

  const minutesOfDay = Math.floor(dayFraction * 24 * 60);

  return {
    absoluteDay,
    dayOfYear,
    month,
    dayOfMonth,
    hour: Math.floor(minutesOfDay / 60),
    minute: minutesOfDay % 60,
    dayFraction,
  };
}

/**
 * The date, as Icelandic writes it: `25. maí`.
 *
 * ⚠️ **The order and the full stop are part of the translation**, not of this
 * function — English would write *25 May* and German *25. Mai*. That is why the
 * whole pattern is one key rather than two pieces glued together here.
 */
export function formatWorldDate(time: WorldTime): string {
  return t('clock.date', {
    day: time.dayOfMonth,
    month: tAnyKey(`${MONTH_KEY_PREFIX}${time.month}`),
  });
}

/** The time of day, `06:00`. */
export function formatWorldClock(time: WorldTime): string {
  return `${String(time.hour).padStart(2, '0')}:${String(time.minute).padStart(2, '0')}`;
}
